import classNames from 'classnames';
import { useFormik } from 'formik';
import { pick } from 'lodash';
import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import * as yup from 'yup';
import { useCurrentUserQuery } from '@/modules/auth/queries/user.tsx';
import { findDefaultValue } from '@/modules/common/utils/default-value.ts';
import {
  AUS_POSTCODE_REGEX,
  NAME_REGEX,
  USA_POSTCODE_REGEX,
  phoneRegExp,
} from '@/modules/common/utils/regex.ts';
import { OnboardingWrapper } from '@/modules/entity/components/OnboardingWrapper.tsx';
import {
  useEntityItemUncachedQuery,
  useUpdateEntityMutation,
} from '@/modules/entity/queries/entity.tsx';
import { auStateOptions } from '@/modules/entity/static/au-states.ts';
import { CountryNameCodeEnum } from '@/modules/entity/static/countries.ts';
import { usaStateOptions } from '@/modules/entity/static/usa-states.ts';
import { EntityItemFullResponse } from '@/modules/entity/types/EntityItemResponse.ts';
import { OnboardingStepProps } from '@/modules/entity/utils/stepper/roadsTypes.ts';
import {
  Button,
  CustomCheckbox,
  CustomSwitch,
  Select,
  SelectButtons,
  SelectWithCreatable,
  TextInputField,
  getSelectOptionByEnum,
  getSelectValueByEnum,
} from 'ui';
import { PlusBlueIcon, TrashIcon } from 'ui/icons';
import { CountryCodeEnum, EntityPreferredContactWay } from 'api';

export interface ContactDetailsFormik {
  contact_name?: string;
  entity_name?: string;
  email?: string;
  phone_number?: string;
  additional_emails?: string[];
  notifications_enabled?: boolean;
  preferred_contact_way?: EntityPreferredContactWay;
  additional_address?: string;
  address?: string;
  suburb?: string;
  state?: string;
  postcode?: string;
  country?: CountryNameCodeEnum | CountryCodeEnum;

  send_information_about_other_investments?: boolean;
}

const validationSchema = yup.object({
  entity_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .nullable(),
  contact_name: yup
    .string()
    .trim()
    .matches(NAME_REGEX, 'Invalid format, only allowed: letters, apostrophes, spaces, hyphens')
    .nullable(),
  address: yup.string().trim().max(80).required('This field is required'),
  additional_address: yup.string().trim().max(80).nullable(),
  phone_number: yup
    .string()
    .trim()
    .matches(
      phoneRegExp,
      "Phone number doesn't match our format, here are a few examples: +61 412 345 678, 11234567890",
    )
    .required('This field is required'),
  email: yup.string().trim().email('Must be a valid email').required('This field is required'),
  additional_emails: yup
    .array()
    .of(yup.string().trim().required('This field is required').email('Must be a valid email'))
    .nullable(),
  notifications_enabled: yup.boolean(),
  send_information_about_other_investments: yup.boolean(),
  preferred_contact_way: yup
    .mixed<EntityPreferredContactWay>()
    .oneOf(Object.values(EntityPreferredContactWay))
    .required('This select is required'),
  suburb: yup.string().trim().required('This field is required'),
  state: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      if (country === 'AUS' || country === 'USA') {
        return schema.required('This field is required');
      }

      return schema.nullable();
    }),
  postcode: yup
    .string()
    .trim()
    .when('country', ([country], schema) => {
      if (country === 'AUS' || country === 'NZL') {
        return schema.matches(AUS_POSTCODE_REGEX, 'Invalid postcode format, e.g. 2000');
      }

      if (country === 'USA') {
        return schema.matches(USA_POSTCODE_REGEX, 'Invalid postcode format, e.g. 20001');
      }

      return schema;
    })
    .required('This field is required'),
  country: yup.string().trim().required('This field is required'),
});

export const ContactDetails = ({ moveForward, entity_type_prefix }: OnboardingStepProps) => {
  const { data: currentUser } = useCurrentUserQuery();
  const location = useLocation();
  const state = location.state;

  const { id: entity_id = 0 } = useParams();
  const { data } = useEntityItemUncachedQuery(+entity_id, entity_id === 0 || isNaN(+entity_id));

  const generateInitialValues = (data?: EntityItemFullResponse): ContactDetailsFormik => {
    const userName =
      (entity_type_prefix === 'individual_entity_details'
        ? data?.entity.entity_name
        : data?.entity?.contact_name ?? data?.entity?.[entity_type_prefix]?.contact_name) ??
      currentUser?.first_name + ' ' + currentUser?.last_name;
    const current = pick(data?.entity, [
      'id',
      'email',
      'phone_number',
      'address',
      'additional_emails',
      'notifications_enabled',
      'preferred_contact_way',
      'suburb',
      'state',
      'postcode',
      'country',
    ]);
    const currentEntity = pick(data?.entity?.[entity_type_prefix], [
      'id',
      'email',
      'phone_number',
      'address',
      'additional_emails',
      'notifications_enabled',
      'preferred_contact_way',
      'suburb',
      'state',
      'postcode',
      'country',
    ]);
    const initialValues: ContactDetailsFormik = {
      ...current,
      ...currentEntity,
      country: current?.country ?? currentEntity?.country ?? state?.country,
      email: data?.entity?.email || currentUser?.email || '',
      notifications_enabled: current?.notifications_enabled ?? true,
      preferred_contact_way: current?.preferred_contact_way ?? EntityPreferredContactWay.Email,
      additional_emails: data?.entity.additional_emails || [],
      send_information_about_other_investments:
        data?.entity.send_information_about_other_investments ?? true,
    };

    if (entity_type_prefix === 'individual_entity_details') {
      return {
        ...initialValues,
        entity_name: userName,
      };
    }

    return {
      ...initialValues,
      contact_name: userName,
    };
  };

  const formik = useFormik<ContactDetailsFormik>({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    validationSchema,
    initialValues: generateInitialValues(data),
    enableReinitialize: true,
    onSubmit: async ({ contact_name, entity_name, ...values }, { setFieldError }) => {
      if (!entity_name && !contact_name) {
        entity_type_prefix === 'individual_entity_details'
          ? setFieldError('entity_name', 'This field is required')
          : setFieldError('contact_name', 'This field is required');
        return;
      }
      if (formik.dirty) {
        const valuesAddress = pick(values, ['address', 'suburb', 'state', 'postcode', 'country']);
        const additionalEmails = values.additional_emails?.filter(item => !!item);
        await updateEntity({
          ...data?.entity,
          ...values,
          ...(entity_type_prefix === 'individual_entity_details'
            ? { entity_name }
            : { contact_name }),
          additional_emails: (additionalEmails?.length || 0) > 0 ? additionalEmails : [],
          [entity_type_prefix]:
            entity_type_prefix === 'individual_entity_details'
              ? { ...data?.entity?.[entity_type_prefix], ...valuesAddress }
              : { ...data?.entity?.[entity_type_prefix], contact_name },
        });
      }
      moveForward && moveForward();
    },
  });

  const { mutateAsync: updateEntity, isLoading } = useUpdateEntityMutation({
    setErrors: formik.setErrors,
  });

  const handleClickSendInvestmentsInformation = (isChecked: boolean) => {
    formik.setFieldValue('send_information_about_other_investments', isChecked);
  };

  const handleClickDeleteButton = () => {
    if (!formik.values.additional_emails) return;
    formik.setFieldValue('additional_emails', [
      ...formik.values.additional_emails.slice(0, formik.values.additional_emails.length - 1),
    ]);
  };

  const TrashButton = (
    <button
      key="trash"
      type="button"
      className="absolute inset-y-0 right-0 flex items-center"
      onClick={handleClickDeleteButton}
    >
      <img src={TrashIcon} alt="delete" />
    </button>
  );

  const registerName = useMemo(() => data?.entity?.registers?.[0]?.name ?? 'this registry', [data]);

  const handleChangeSelect = (
    field: string,
    value: string | boolean | EntityPreferredContactWay,
  ) => {
    formik.setFieldValue(field, value);
    formik.setFieldError(field, '');
  };

  return (
    <OnboardingWrapper
      title="Contact details"
      onClickContinue={formik.handleSubmit}
      formik={formik}
      isLoading={isLoading}
    >
      <>
        <div className="flex flex-col sm:gap-8 gap-4">
          {/* <div className="flex gap-4 flex-wrap"> */}
          {/*   <div> */}
          {/*     <Button variant="secondary" active={true}> */}
          {/*       {registerName} */}
          {/*     </Button> */}
          {/*   </div> */}
          {/* </div> */}

          <TextInputField
            name={
              entity_type_prefix === 'individual_entity_details' ? 'entity_name' : 'contact_name'
            }
            label={'Contact name'}
          />

          <div className="flex flex-col gap-1">
            <span
              className={classNames(
                'flex flex-row justify-between text-[14px] leading-[20px]',
                'font-[Inter] text-gray-900 font-medium',
              )}
            >
              Preferred way to communicate with {registerName}
            </span>
            <span
              className={classNames(
                'flex flex-row justify-between text-[12px] leading-[20px]',
                'font-[Inter] text-gray-600 font-medium',
              )}
            >
              The issuer will use this channel to send you all communications
            </span>
            <SelectButtons
              options={getSelectOptionByEnum<EntityPreferredContactWay>(EntityPreferredContactWay)}
              selected={getSelectValueByEnum<EntityPreferredContactWay>(
                formik.values.preferred_contact_way ?? '',
                EntityPreferredContactWay,
              )}
              onChange={value => {
                handleChangeSelect('preferred_contact_way', value);
              }}
              error={formik.errors.preferred_contact_way}
            />
          </div>
          <CustomSwitch
            checked={formik.values.notifications_enabled}
            onChange={() => {
              formik.setFieldValue('notifications_enabled', !formik.values.notifications_enabled);
            }}
            className="text-[14px]"
            label="Automatic email notifications"
            subLabel="Compli One will automatically notify you about transaction status updated, payment requests, transfers and dividends"
          />
          <TextInputField name="email" label="Email address" placeholder="Email" />
          <TextInputField name="phone_number" label="Phone number" placeholder="Phone number" />

          <span className="text-[16px] leading-6 text-gray-900 font-[Inter] font-[500]">
            Postal address
          </span>

          <TextInputField
            name="address"
            label="Address Line 1 (Street Address)"
            placeholder="Address"
          />

          <TextInputField
            name="additional_address"
            label="Address Line 2 (Optional)"
            placeholder="Address"
          />

          <div className="w-full flex sm:flex-row flex-col items-start gap-2">
            <TextInputField label="postcode" name="postcode" />
            <Select
              isClearable
              label="Country"
              options={getSelectOptionByEnum<CountryNameCodeEnum>(CountryNameCodeEnum, true)}
              value={formik.values.country as unknown as CountryNameCodeEnum}
              onChange={value => handleChangeSelect('country', value || '')}
              error={formik.errors.country}
              positionMenu="up"
            />
          </div>

          <div className="w-full flex sm:flex-row flex-col items-start gap-2">
            <TextInputField name="suburb" label="Suburb/City" />
            <SelectWithCreatable
              isClearable={true}
              withCreatable={true}
              label="State/Territory"
              key={`state_country_${formik.values.country}`}
              options={
                (formik.values.country as unknown as CountryNameCodeEnum) === 'AUS'
                  ? auStateOptions
                  : (formik.values.country as unknown as CountryNameCodeEnum) === 'USA'
                  ? usaStateOptions
                  : []
              }
              value={
                findDefaultValue(
                  (formik.values.country as unknown as CountryNameCodeEnum) === 'AUS'
                    ? auStateOptions
                    : (formik.values.country as unknown as CountryNameCodeEnum) === 'USA'
                    ? usaStateOptions
                    : [],
                  formik.values.state,
                ) || { value: formik.values.state, label: formik.values.state }
              }
              onChange={option => handleChangeSelect('state', option?.value || '')}
              error={formik.errors.state}
              positionMenu="up"
            />
          </div>

          <div className="flex sm:flex-row flex-col gap-2 w-full justify-between sm:items-center items-start">
            <span className="font-[Inter] flex flex-col">
              <span className="text-gray-900 text-[16px] leading-6 font-[500]">
                Additional emails
              </span>
              <span className="text-gray-600 text-[12px] leading-5">
                For Advisors, accountants or anyone else who needs updates
              </span>
            </span>

            <div className="w-[130px]">
              <Button
                variant="plain"
                className="px-0"
                onClick={() => {
                  formik.setFieldValue('additional_emails', [
                    ...(formik.values.additional_emails || []),
                    '',
                  ]);
                  formik.setErrors({});
                }}
              >
                <img src={PlusBlueIcon} alt="plus" className="pr-1" /> Add new email
              </Button>
            </div>
          </div>

          <div className="flex flex-col gap-4">
            <>
              {(formik.values.additional_emails || []).map((_, i, { length }) => (
                <TextInputField
                  key={`additional_emails.${i}`}
                  name={`additional_emails.${i}`}
                  placeholder="Enter email"
                  buttons={length - 1 === i ? [TrashButton] : undefined}
                />
              ))}
            </>
          </div>
        </div>
        <CustomCheckbox
          label={`I/We do not want ${registerName} to send me investment information about its other financial products and services`}
          checked={!formik.values.send_information_about_other_investments}
          onChange={e => handleClickSendInvestmentsInformation(!e.target.checked)}
        />
        <p className="text-gray-600 text-[12px] leading-5 mt-10">
          By providing your email address, you elect to receive all communications electronically by
          the Compli One share registry (where legally permissible).
        </p>
      </>
    </OnboardingWrapper>
  );
};
